import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const SpendOverTimeChart = ({ transactions, granularity = 'daily' }) => {
  // Group transactions by date or month
  const groupedData = transactions.reduce((acc, txn) => {
    const date = new Date(txn.transactionDate);
    let key;

    if (granularity === 'monthly') {
      key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    } else {
      // Daily
      key = date.toISOString().split('T')[0];
    }

    if (!acc[key]) {
      acc[key] = {
        date: key,
        totalSpend: 0,
        transactionCount: 0
      };
    }

    acc[key].totalSpend += parseFloat(txn.amount || 0);
    acc[key].transactionCount += 1;

    return acc;
  }, {});

  // Convert to array and sort by date
  const chartData = Object.values(groupedData).sort((a, b) =>
    a.date.localeCompare(b.date)
  );

  // Format date for display
  const formatDate = (dateStr) => {
    if (granularity === 'monthly') {
      const [year, month] = dateStr.split('-');
      const date = new Date(year, parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } else {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="text-sm font-semibold text-gray-900">
            {formatDate(payload[0].payload.date)}
          </p>
          <p className="text-sm text-gray-700 mt-1">
            Total Spend: <span className="font-semibold">{formatCurrency(payload[0].value)}</span>
          </p>
          <p className="text-sm text-gray-600">
            Transactions: {payload[0].payload.transactionCount}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Spend Over Time</h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="date"
            tickFormatter={formatDate}
            tick={{ fontSize: 12, fill: '#6b7280' }}
            stroke="#9ca3af"
          />
          <YAxis
            tickFormatter={formatCurrency}
            tick={{ fontSize: 12, fill: '#6b7280' }}
            stroke="#9ca3af"
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Line
            type="monotone"
            dataKey="totalSpend"
            stroke="#3b82f6"
            strokeWidth={2}
            name="Total Spend"
            dot={{ fill: '#3b82f6', r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SpendOverTimeChart;
